import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import { Button } from '@/components/ui/button.tsx';
import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { useCreateFeedbackQuery } from '@/features/feedback/hooks/mutations/create-feedback.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useTranslation } from 'react-i18next';

const getFeedbackSchema = (translation?: {
  typeRequired: string;
  descriptionRequired: string;
}) => {
  const messages = translation ?? {
    typeRequired: 'Type is required',
    descriptionRequired: 'Description is required',
  };

  return z.object({
    type: z.enum(['suggestion', 'problem', 'compliment'], {
      error: messages.typeRequired,
    }),
    description: z
      .string({ error: messages.descriptionRequired })
      .min(1, { message: messages.descriptionRequired }),
  });
};

export const FeedbackDialog = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const formSchema = getFeedbackSchema({
    typeRequired: t('feedback.form.type.required'),
    descriptionRequired: t('feedback.form.description.required'),
  });

  type TFeedbackFormSchema = z.infer<typeof formSchema>;

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: 'suggestion',
      description: '',
    } as const,
  });

  const createFeedbackMutation = useCreateFeedbackQuery({
    onSuccess: () => {
      setOpen(false);

      form.reset();
    },
  });

  function onSubmit(data: TFeedbackFormSchema) {
    createFeedbackMutation.mutate(data);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="text-left" asChild>
        {children}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <DialogTitle>{t('feedback.title')}</DialogTitle>
              <DialogDescription>{t('feedback.description')}</DialogDescription>
            </div>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="required">
                        {t('feedback.form.type.label')}
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="suggestion" id="suggestion" />
                            <Label htmlFor="suggestion">
                              {t('feedback.form.type.options.suggestion')}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="problem" id="problem" />
                            <Label htmlFor="problem">
                              {t('feedback.form.type.options.problem')}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="compliment" id="compliment" />
                            <Label htmlFor="compliment">
                              {t('feedback.form.type.options.compliment')}
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/*  */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="required">
                        {t('feedback.form.description.label')}
                      </FormLabel>
                      <FormControl>
                        <Textarea className="h-32 resize-none" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end">
                  <Button className="flex min-w-28 items-center gap-2">
                    {createFeedbackMutation.isPending && <LoadingSpinner size={24} />}
                    <span className="text-[16px] select-none">
                      {t('common.buttons.submitButton')}
                    </span>
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
